import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardActions,
  Button,
  Grid,
  Chip,
} from '@mui/material';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import { useGame } from '../../contexts/GameContext';

const HomePage = ({ onNavigate }) => {
  const { getStats } = useGame();
  const stats = getStats();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#58CC02' }}>
          🎵 Every Voice Can Sing
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Learn Indian classical music through fun, interactive games!
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mt: 2 }} justifyContent="center">
        {/* Play Mode Card */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card 
            sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #58CC02 0%, #4DB802 100%)',
              color: 'white',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 8px 24px rgba(88, 204, 2, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SportsEsportsIcon sx={{ fontSize: 48, mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  PLAY
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom>
                Game Mode - Learn by Playing!
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                Complete fun levels, earn stars, and unlock new challenges. 
                Perfect for beginners and anyone who loves games!
              </Typography>
              
              {stats.totalLevelsCompleted > 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${stats.totalLevelsCompleted} Levels`} 
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                  <Chip 
                    label={`⭐ ${stats.totalStars} Stars`} 
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                  <Chip 
                    label={`${stats.averageScore}% Avg`} 
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button 
                fullWidth 
                variant="contained" 
                size="large"
                onClick={() => onNavigate('play')}
                sx={{ 
                  backgroundColor: 'white',
                  color: '#58CC02',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                }}
              >
                Start Playing →
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Nāda Sound Meditation Card */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card
            sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #1a0533 0%, #3b1060 50%, #5b21b6 100%)',
              color: 'white',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SelfImprovementIcon sx={{ fontSize: 48, mr: 2, color: '#c084fc' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 300, letterSpacing: 4 }}>
                    Nāda
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a78bfa', letterSpacing: 3, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Sound Meditation
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
                Hold sacred notes in stillness. No scores, no levels — just you and the sound.
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => onNavigate('nada')}
                sx={{
                  borderColor: '#c084fc',
                  color: '#c084fc',
                  fontWeight: 600,
                  letterSpacing: 2,
                  '&:hover': { borderColor: '#a855f7', background: '#ffffff10' },
                }}
              >
                Begin →
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Info Section */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Why Learn with EVCS?
        </Typography>
        <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="h3">🎮</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
                Fun & Engaging
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Learn through gameplay, not boring exercises
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="h3">🎯</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
                Real-Time Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See your pitch accuracy instantly
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="h3">🏆</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
                Track Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Earn stars and unlock new levels
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default HomePage;
